'use client';

import React, { useEffect, useState } from 'react';
import { Book, Plus, ChevronDown, ChevronRight, GripVertical, Crown } from 'lucide-react';
import Link from 'next/link';
import { Show, useUser } from '@clerk/nextjs';

const ADMIN_ID = "user_3AtnbF82V4ROWYZ4rH2PlrSsvGk";

export default function CodeHub() {
    const { user } = useUser();
    const [policies, setPolicies] = useState<any[]>([]);
    const [chapters, setChapters] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [newChapterName, setNewChapterName] = useState('');
    const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

    //const [isAdmin, setIsAdmin] = useState(false);

    const isAdmin = user?.id === ADMIN_ID; //isEligibleAdmin

    const fetchData = async () => {
        try {
            const [billsRes, chaptersRes] = await Promise.all([
                fetch('/api/bills/effective/list/old', { cache: 'no-store' }),
                fetch('/api/bills/chapters/full', { cache: 'no-store' })
            ]);

            const billsData = await billsRes.json();
            const chaptersData = await chaptersRes.json();

            if (Array.isArray(billsData)) {
                const sorted = [...billsData].sort((a, b) =>
                     new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                );
                setPolicies(sorted);
            }
            if (Array.isArray(chaptersData)) setChapters(chaptersData);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const addChapter = async () => {
        if (!newChapterName) return;
        try {
            const response = await fetch('/api/bills/create-chapter', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: newChapterName })
            });
            if (response.ok) {
                setNewChapterName('');
                fetchData();
            }
        } catch (error) {
            console.error('Failed to create:', error);
        }
    };

    const toggleCollapse = (chapterId: string) => {
        setCollapsed(prev => ({ ...prev, [chapterId]: !prev[chapterId] }));
    };

    const handleDragStart = (e: React.DragEvent, billId: string) => {
        if (!isAdmin) return;
        e.dataTransfer.setData("billId", billId);
    };

    const handleDragOver = (e: React.DragEvent) => {
        if (!isAdmin) return;
        e.preventDefault();
    };

    const handleDropOnChapter = async (e: React.DragEvent, targetTagName: string) => {
        if (!isAdmin) return;
        e.preventDefault();
        e.stopPropagation();
        const billId = e.dataTransfer.getData("billId");

        const updatedPolicies = policies.map(p =>
            p.id === billId ? { ...p, tags: [targetTagName] } : p
        );

        const updates = updatedPolicies.map((p, i) => ({
            id: p.id,
            position: i,
            tags: p.tags,
            title: p.title,
            category: p.category
        }));

        try {
            const response = await fetch('/api/bills/update-position', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ updates })
            });
            if (response.ok) fetchData();
        } catch (error) {
            console.error('Chapter update failed:', error);
        }
    };

    const handleDropOnArticle = async (e: React.DragEvent, targetPolicy: any) => {
        if (!isAdmin) return;
        e.preventDefault();
        e.stopPropagation();

        const draggedId = e.dataTransfer.getData('billId');
        if (draggedId === targetPolicy.id) return;

        const reordered = [...policies];
        const draggedIdx = reordered.findIndex(p => p.id === draggedId);
        const targetIdx = reordered.findIndex(p => p.id === targetPolicy.id);

        const [movedItem] = reordered.splice(draggedIdx, 1);
        movedItem.tags = targetPolicy.tags;
        reordered.splice(targetIdx, 0, movedItem);

        setPolicies(reordered);

        const updates = reordered.map((p, i) => ({
            id: p.id,
            position: i,
            tags: p.tags,
            title: p.title,
            category: p.category
        }));

        try {
            await fetch('/api/bills/update-position', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ updates })
            });
        } catch (error) {
            console.error(error);
            fetchData();
        }
    };

    if (loading) return <div className="min-h-screen flex items-center bg-amber-50 bg-[#fcfaf7] justify-center font-serif italic text-slate-500">Retrieving Official Records from the Archive...</div>;

    const miscPolicies = policies.filter(p => !p.tags || p.tags.length === 0 || !chapters.find(c => c.name === p.tags[0]));

    return (
        <div className="min-h-screen bg-amber-50 bg-[#fcfaf7] text-slate-900 font-serif selection:bg-amber-200">
            <div className="flex mx-auto">
                
             {/*} <Show when={(has) => isAdmin} >
                            
                    <Link
                        href="/holy-seat/convention-of-fathers"
                        className="mt-6 ml-auto mr-10 -mb-10 px-4 py-2 bg-orange-200 text-orange-900 font-sans text-xs font-bold uppercase rounded hover:bg-orange-300"
                    >
                        Convention of Fathers
                    </Link>
                </Show>*/}
            </div> 
            <div className="max-w-5xl mx-auto px-8 py-20">

                <header className="border-b-4 border-double border-slate-800 pb-10 mb-10 text-center">
                   <div className="flex justify-center mb-6">
                        <Crown size={48} className="text-slate-800" />
                    </div>
                    <h1 className="text-5xl font-black tracking-tighter mb-0">Chronological Resolutions of the Holy Seat</h1>
                   {/*} <p className="text-lg uppercase tracking-[0.2em] font-sans font-semibold text-slate-600 mb-2">Official Chronological Laws, Rules, and Resolutions</p>
                    <p className="text-sm font-sans text-slate-500">Laws - Holy Empire of American Code - Chronological </p> */}


                </header>

              
                <div className="bg-amber-100 border border-slate-200 shadow-sm p-8">
                    <ul className="space-y-4">
                        {policies.map((policy, idx) => (
                            <li
                                key={policy.id}
                                className="flex items-baseline gap-4 group border-b border-slate-200/50 last:border-0 pb-3 last:pb-0"
                            >
                              

                                

                               {/*  <span className="font-sans font-bold text-slate-700 w-20 shrink-0 text-xs">
                                  Resolution {policies.length - idx}
                                </span>
                                <div className={`text-white font-sans px-2 font-bold text-[10px] py-0.5 rounded shrink-0 ${policy.category === 'Moral' ? 'bg-red-800' : ''} ${policy.category === 'Introductory' ? 'bg-orange-500' : ''} ${policy.category === 'General' ? 'bg-blue-800' : ''} ${policy.category === 'Divine' ? 'bg-purple-800' : ''} ${!['Moral', 'General', 'Divine', 'Introductory'].includes(policy.category) ? 'bg-slate-700' : ''}`}>
                                    {policy.category === 'General' ? "Human" : policy.category === 'Divine' "Eternal" : policy.category === 'Moral' ? 'Religious' : policy.category}
                                </div>
                                 <div className="bg-slate-200 text-slate-700 font-sans px-2 font-bold text-[10px] py-0.5 rounded shrink-0 uppercase">
                                    {policy.tags?.[0] || 'General'}
                                </div> 
                          
                                */} 
<span className="font-sans font-bold text-slate-700 w-20 shrink-0 text-xs">
                                  H.S. {policies.length - idx}
                                </span> 
                                <Link
                                    href={`/law/${policy.slug}`}
                                    className="hover:text-amber-800 hover:underline decoration-amber-500 decoration-2 transition-colors"
                                >
                                          {policy.title}
                                </Link>

                                <span className="text-[9px] font-sans text-slate-400 ml-auto uppercase tracking-tighter">
                                    {new Date(policy.created_at).toLocaleDateString()}
                                </span>
                            </li> 
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    ); 
}